import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { FullPageLoader, PageProgressBar } from "@/components/loading";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { StudentHeader } from "@/components/student-header";
import { NotificationBell } from "@/components/notification-bell";
import { useAuth } from "@/hooks/use-auth";
import { useStaffHeartbeat } from "@/hooks/use-staff-heartbeat";
import { getAccessToken } from "@/lib/api/client";
import { requireAdminRole } from "@/lib/auth-server-fn";
import { updateLastSeen } from "@/lib/api/users";
import { Separator } from "@/components/ui/separator";
import { BreadcrumbNav } from "@/components/breadcrumb-nav";
import { ThemeToggle } from "@/components/ThemeToggle";
import { InstallAppButton } from "@/components/install-app-button";
import { CommandSearch } from "@/components/command-search";
import { bidiText, playNotificationSound } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Megaphone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { usePendingAcknowledgments } from "@/hooks/use-acknowledgments";

export const Route = createFileRoute("/_authenticated")({
  head: () => ({
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  beforeLoad: async ({ location }) => {
    // Skip auth checks during SSR - will be checked on client hydration (mirrors
    // the pattern in _authenticated/admin.tsx and _authenticated/my-team.tsx).
    if (typeof window === "undefined") {
      return { auth: null };
    }

    const token = getAccessToken();
    if (!token) {
      throw redirect({ to: "/login", search: { redirect: location.href }, replace: true });
    }

    // Validate the token server-side via the same shared helper used by admin.tsx/my-team.tsx.
    // An invalid/expired token decodes with no userId, so this also catches stale tokens that
    // are merely present in localStorage.
    const auth = await requireAdminRole({ data: token });
    if (!auth.userId) {
      throw redirect({ to: "/login", search: { redirect: location.href }, replace: true });
    }

    // Handed down as router context so the nested guards (admin, my-team, team-leader) can reuse
    // THIS validation instead of each re-calling the same server function on the same navigation.
    // Router runs every matched segment's beforeLoad per navigation, so re-deriving the roles in
    // each child is what previously fired the identical _serverFn POST two or more times per
    // route change — enough, combined with the other admin traffic, to trip the API rate limiter.
    return { auth };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { loading, user, hasPermission, isAdmin, isContentCreator, isTeamLeader } =
    useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // `location.search` from useLocation() is the PARSED search OBJECT, not a query
  // string. Passing it to `new URLSearchParams(...)` produced a permanently empty
  // set, so isExamActive was always false and the sidebar/header never hid during
  // an exam. Read the typed search off the current match instead; `strict: false`
  // is required because this layout renders above every authenticated route, not
  // just /exams.
  const search = useSearch({ strict: false }) as { active?: unknown } | undefined;
  const isExamActive =
    location.pathname === "/exams" && (search?.active === true || search?.active === "true");
  // Student layout applies when the user holds none of the staff roles (SystemAdmin,
  // ContentCreator, TeamLeader) — a real role check, not a permission-absence guess.
  const isStudent = !isAdmin && !isContentCreator && !isTeamLeader;

  // One 60s pulse per monitored employee, mounted once for the whole authenticated shell so it
  // survives client-side navigation instead of restarting per route.
  useStaffHeartbeat();

  useEffect(() => {
    if (!loading && !user) {
      navigate({ to: "/login", search: { redirect: location.href }, replace: true });
    }
  }, [loading, user, navigate, location.href]);

  // Token invalidation redirects are handled in use-auth.tsx via global auth:unauthorized event
  // No need to listen to onAuthStateChange here anymore.

  // Keep-alive updater to track user online status
  const userIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    if (userIdRef.current === user.id) return;
    userIdRef.current = user.id;

    let stopped = false;

    const pingLastSeen = async () => {
      try {
        const res = await updateLastSeen(user.id);
        if (!res) {
          // Column doesn't exist, RLS blocks it, or schema mismatch — stop retrying
          console.warn("last_seen_at update failed (disabling)");
          stopped = true;
        }
      } catch (err) {
        console.warn("last_seen_at update error:", err);
        stopped = true;
      }
    };

    pingLastSeen();
    const interval = setInterval(() => {
      if (!stopped) pingLastSeen();
    }, 60000);
    return () => {
      clearInterval(interval);
      userIdRef.current = null;
    };
  }, [user?.id]);

  // ── Required-acknowledgment system (uses get_pending_acknowledgments RPC) ──
  const {
    pending: pendingAcks,
    acknowledge: acknowledgeAck,
    isAcknowledging,
  } = usePendingAcknowledgments();

  // Show oldest first (array is newest-first, so last element is oldest)
  const activeAnnouncement =
    !hasPermission("manage:users") && pendingAcks.length > 0
      ? pendingAcks[pendingAcks.length - 1]
      : null;

  // Play notification chime sound when a required announcement pop-up is shown
  useEffect(() => {
    if (activeAnnouncement?.message_id) {
      playNotificationSound();
    }
  }, [activeAnnouncement?.message_id]);

  const acknowledgeMessage = () => {
    if (!activeAnnouncement) return;
    acknowledgeAck(activeAnnouncement.message_id);
    toast.success("Announcement acknowledged");
  };

  const isNavigating = useRouterState({ select: (s) => s.status === "pending" });

  if (loading) {
    return (
      <div suppressHydrationWarning>
        <FullPageLoader />
      </div>
    );
  }

  // Student specific Layout with Top Navigation Header
  if (isStudent) {
    return (
      <div className="flex flex-col min-h-screen bg-background student-theme">
        <PageProgressBar active={isNavigating} />
        {!isExamActive && <StudentHeader />}
        <main
          className={`flex-1 overflow-auto min-w-0 ${
            isExamActive ? "bg-background min-h-screen p-4 md:p-6" : "p-4 md:p-6"
          }`}
        >
          <Outlet />
        </main>

        {/* Forced Acknowledgment Dialog */}
        {activeAnnouncement && (
          <Dialog open={true} onOpenChange={() => {}}>
            <DialogContent
              className="max-w-md p-6 bg-card border rounded-xl shadow-lg [&>button]:hidden"
              onPointerDownOutside={(e) => e.preventDefault()}
              onEscapeKeyDown={(e) => e.preventDefault()}
            >
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                  <Megaphone className="h-5 w-5 text-destructive animate-bounce" />
                  Required Announcement
                </DialogTitle>
              </DialogHeader>

              <div className="py-4 space-y-4">
                {activeAnnouncement.title && (
                  <p className="text-base font-semibold text-foreground">
                    {activeAnnouncement.title}
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Posted on {new Date(activeAnnouncement.created_at).toLocaleString()}
                </p>
                <div
                  {...bidiText}
                  className="rounded-lg bg-muted/40 p-4 text-sm text-foreground whitespace-pre-wrap break-words leading-relaxed max-h-60 overflow-y-auto border"
                >
                  {activeAnnouncement.content}
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  You must acknowledge that you have read and understood this announcement before
                  you can proceed.
                </p>
              </div>

              <div className="flex justify-end gap-3 mt-2">
                <Button
                  className="w-full flex items-center justify-center gap-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold"
                  onClick={acknowledgeMessage}
                  disabled={isAcknowledging}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {isAcknowledging ? "Confirming…" : "I Acknowledge & Understand"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  // Admin / Team Leader Layout with Sidebar Navigation
  return (
    <SidebarProvider>
      <PageProgressBar active={isNavigating} />
      {!isExamActive && <AppSidebar />}
      <SidebarInset className="min-w-0">
        {!isExamActive && (
          <header className="flex h-14 shrink-0 items-center justify-between border-b px-4 gap-4">
            <div className="flex items-center gap-2 min-w-0">
              <SidebarTrigger className="-ml-1" />
              <Separator orientation="vertical" className="mr-2 h-4" />
              <BreadcrumbNav />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <CommandSearch />
              <InstallAppButton />
              <ThemeToggle />
              <NotificationBell />
            </div>
          </header>
        )}
        <main
          className={`flex-1 overflow-auto min-w-0 ${
            isExamActive ? "bg-background min-h-screen p-4 md:p-6" : "p-4 md:p-6"
          }`}
        >
          <Outlet />
        </main>
      </SidebarInset>

      {activeAnnouncement && (
        <Dialog open={true} onOpenChange={() => {}}>
          <DialogContent
            className="max-w-md p-6 bg-card border rounded-xl shadow-lg [&>button]:hidden"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                <Megaphone className="h-5 w-5 text-red-500 animate-bounce" />
                Required Announcement
              </DialogTitle>
            </DialogHeader>

            <div className="py-4 space-y-4">
              {activeAnnouncement.title && (
                <p className="text-base font-semibold text-foreground">
                  {activeAnnouncement.title}
                </p>
              )}
              <p className="text-[10px] text-muted-foreground">
                Posted on {new Date(activeAnnouncement.created_at).toLocaleString()}
              </p>
              <div
                {...bidiText}
                className="rounded-lg bg-muted/40 p-4 text-sm text-foreground whitespace-pre-wrap break-words leading-relaxed max-h-60 overflow-y-auto border"
              >
                {activeAnnouncement.content}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                You must acknowledge that you have read and understood this announcement before you
                can proceed.
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-2">
              <Button
                className="w-full flex items-center justify-center gap-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold"
                onClick={acknowledgeMessage}
                disabled={isAcknowledging}
              >
                <CheckCircle2 className="h-4 w-4" />
                {isAcknowledging ? "Confirming…" : "I Acknowledge & Understand"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </SidebarProvider>
  );
}
